<template>
  <section
    ref="rootRef"
    class="first-step-hero"
    :class="{
      'first-step-hero--single-slide': !hasMultipleSlides,
      'first-step-hero--products': isProductCarousel,
    }"
    data-first-step-hero
  >
    <div class="first-step-hero__inner">
      <div class="first-step-hero__header">
        <h2 class="first-step-hero__heading">{{ heading }}</h2>
        <p class="first-step-hero__subtitle">{{ subtitle }}</p>
      </div>
      <div class="first-step-hero__slider-wrap">
        <Splide
          v-if="localSlides.length > 0"
          :key="'slider-' + localSlides.length"
          :options="splideOptions"
          class="first-step-hero__splide"
          :aria-label="isProductCarousel ? 'Popular products' : 'Success stories'"
        >
          <SplideSlide
            v-for="(slide, index) in localSlides"
            :key="index"
            class="first-step-hero__slide"
          >
            <div class="first-step-hero__before-after">
              <!-- Product carousel: single image per slide -->
              <div
                v-if="isProductCarousel"
                class="first-step-hero__product-inner"
              >
                <img
                  :src="slide.image"
                  :alt="slide.alt || 'Product'"
                  class="first-step-hero__img first-step-hero__img--product"
                >
                <div
                  v-if="slide.testimonial"
                  class="first-step-hero__testimonial-tag first-step-hero__testimonial-tag--elongated"
                >
                  {{ slide.testimonial }}
                </div>
              </div>
              <!-- Before/after slider -->
              <div
                v-else
                class="first-step-hero__before-after-inner"
                :style="{ '--clip-percent': slide.position }"
                :data-slide-index="index"
              >
                <img
                  :src="slide.before"
                  :alt="slide.altBefore || 'Before'"
                  class="first-step-hero__img first-step-hero__img--before"
                >
                <img
                  :src="slide.after"
                  :alt="slide.altAfter || 'After'"
                  class="first-step-hero__img first-step-hero__img--after"
                  :style="{ clipPath: 'inset(0 0 0 ' + slide.position + '%)' }"
                >
                <div
                  class="first-step-hero__handle"
                  :style="{ left: slide.position + '%' }"
                  :data-slide-index="index"
                  role="slider"
                  :aria-valuenow="slide.position"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  tabindex="0"
                  @mousedown.prevent="startDrag($event)"
                  @touchstart.prevent="startDrag($event)"
                  @keydown="handleKeydown($event)"
                >
                  <span class="first-step-hero__handle-icon" aria-hidden="true">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 18L5 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M15 18L19 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </span>
                </div>
              </div>
              <div
                v-if="!isProductCarousel && slide.testimonial"
                class="first-step-hero__testimonial-tag"
              >
                {{ slide.testimonial }}
              </div>
            </div>
          </SplideSlide>
        </Splide>
      </div>
      <div class="first-step-hero__content">
        <ul class="first-step-hero__list">
          <li
            v-for="(item, index) in benefits"
            :key="index"
            class="first-step-hero__list-item"
          >
            <span class="first-step-hero__check" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="9.5" fill="white" stroke="currentColor" stroke-width="1.5" />
                <path d="M7 11L10 14L16 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none" />
              </svg>
            </span>
            <span v-html="item" />
          </li>
        </ul>
        <a
          :href="ctaUrl || '#'"
          class="first-step-hero__cta"
        >
          {{ ctaText }}
        </a>
        <p
          v-if="disclaimer"
          class="first-step-hero__disclaimer"
          v-html="disclaimer"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/splide/dist/css/splide-core.min.css';
import { ref, watch, onMounted, computed, useAttrs } from 'vue';

type BeforeAfterSlide = {
  before: string;
  after: string;
  altBefore?: string;
  altAfter?: string;
  testimonial?: string;
  position: number;
};

type ProductSlide = {
  image: string;
  alt?: string;
  testimonial?: string;
};

type SlideData = BeforeAfterSlide | ProductSlide;

const props = withDefaults(
  defineProps<{
    heading?: string;
    subtitle?: string;
    benefits?: string[];
    ctaText?: string;
    ctaUrl?: string;
    disclaimer?: string;
    slides?: SlideData[];
    variant?: 'products' | 'before-after';
  }>(),
  {
    heading: 'Take your first step today.',
    subtitle: 'Starting at $89 per month.',
    benefits: () => [
      'Medication assisted weight loss with brands like Ozempic®† & Mounjaro®†',
      'Non Insurance Options',
      'Same day provider visits',
      'Up to 31% less body fat mass*',
      'Personal coaching options',
      '100% online consultations',
    ],
    ctaText: 'Get started',
    ctaUrl: '#',
    disclaimer: '*JRNYS members following our tirzepatide protocol experienced outcomes consistent with the Surpass-1 trial, which showed up to a 31% reduction in fat mass over a 6-month period. Results may vary.',
    slides: () => [],
  },
);

const ctaUrl = ref(props.ctaUrl);
const localSlides = ref<SlideData[]>([]);
const heading = ref(props.heading);
const subtitle = ref(props.subtitle);
const disclaimer = ref(props.disclaimer);
const benefits = ref<string[]>(props.benefits);
const variant = ref<'products' | 'before-after'>(props.variant ?? 'before-after');

const isProductCarousel = computed(() => variant.value === 'products');
const clampPosition = (p: number) => Math.max(5, Math.min(95, p ?? 50));

function isBeforeAfterSlide(s: SlideData): s is BeforeAfterSlide {
  return 'before' in s && 'after' in s;
}

function parseSlidesFromSource(source: unknown): SlideData[] {
  if (!source) return [];
  try {
    const parsed = (typeof source === 'string' ? JSON.parse(source) : source) as SlideData[];
    return (parsed || []).map((s) => {
      if (isBeforeAfterSlide(s)) {
        return { ...s, position: clampPosition((s as BeforeAfterSlide).position ?? 50) };
      }
      return s;
    });
  } catch {
    return [];
  }
}

// Initialize from props.slides
watch(
  () => props.slides,
  (newSlides) => {
    localSlides.value = parseSlidesFromSource(newSlides);
  },
  { immediate: true },
);

// Read from attrs (data-slides) - Vue passes these from server-rendered HTML
const attrs = useAttrs();
if (localSlides.value.length === 0 && attrs['data-slides']) {
  localSlides.value = parseSlidesFromSource(attrs['data-slides']);
}
// Fallback: ensure we always have slides for testing
if (localSlides.value.length === 0) {
  localSlides.value = [{
    before: '/assets/img/treatments/trusted-experts.webp',
    after: '/assets/img/treatments/same-day-appointments.webp',
    altBefore: 'Before',
    altAfter: 'After',
    testimonial: 'Audrey lost 53lbs. with JRNYS',
    position: 50,
  }];
}

const hasMultipleSlides = computed(() => localSlides.value.length > 1);
const splideOptions = computed(() => ({
  type: 'loop',
  perPage: 1,
  arrows: isProductCarousel.value ? true : hasMultipleSlides.value,
  pagination: isProductCarousel.value ? false : hasMultipleSlides.value,
  drag: true,
  gap: 0,
  breakpoints: {
    768: {
      arrows: isProductCarousel.value ? true : hasMultipleSlides.value,
      pagination: isProductCarousel.value ? false : hasMultipleSlides.value,
    },
  },
}));

let moveHandler: ((e: MouseEvent | TouchEvent) => void) | null = null;
let upHandler: (() => void) | null = null;

function startDrag(e: MouseEvent | TouchEvent) {
  const handle = (e.currentTarget || e.target) as HTMLElement;
  const container = handle.closest('.first-step-hero__before-after-inner');
  const indexStr = handle.getAttribute('data-slide-index') ?? container?.getAttribute('data-slide-index') ?? '';
  const index = parseInt(indexStr, 10) || 0;
  if (!container || index >= localSlides.value.length) return;

  const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
    const slide = localSlides.value[index];
    if (!slide || !isBeforeAfterSlide(slide)) return;
    const x = 'touches' in moveEvent ? moveEvent.touches[0].clientX : moveEvent.clientX;
    const rect = container.getBoundingClientRect();
    // Clamp to 5–95% so the handle stays visible and isn't clipped at edges
    const percent = Math.max(5, Math.min(95, ((x - rect.left) / rect.width) * 100));
    slide.position = percent;
  };

  const handleUp = () => {
    if (moveHandler) {
      document.removeEventListener('mousemove', moveHandler as (e: Event) => void);
      document.removeEventListener('touchmove', moveHandler as (e: Event) => void);
    }
    if (upHandler) {
      document.removeEventListener('mouseup', upHandler);
      document.removeEventListener('touchend', upHandler);
    }
  };

  moveHandler = handleMove;
  upHandler = handleUp;
  document.addEventListener('mousemove', moveHandler as (e: Event) => void);
  document.addEventListener('mouseup', handleUp);
  document.addEventListener('touchmove', moveHandler as (e: Event) => void, { passive: true });
  document.addEventListener('touchend', handleUp);

  handleMove(e);
}

function handleKeydown(e: KeyboardEvent) {
  const handle = e.currentTarget as HTMLElement;
  const indexStr = handle.getAttribute('data-slide-index');
  const index = indexStr !== null ? parseInt(indexStr, 10) : 0;
  const step = e.shiftKey ? 10 : 5;
  const slide = localSlides.value[index];
  if (!slide || !isBeforeAfterSlide(slide)) return;
  if (e.key === 'ArrowLeft') {
    slide.position = Math.max(5, slide.position - step);
  } else if (e.key === 'ArrowRight') {
    slide.position = Math.min(95, slide.position + step);
  }
}

const rootRef = ref<HTMLElement | null>(null);

onMounted(() => {
  const host = rootRef.value?.closest?.('first-step-hero') ?? rootRef.value?.parentElement ?? rootRef.value;
  if (host) {
    const data = (host as HTMLElement).dataset;
    if (data.seeAllUrl) ctaUrl.value = data.seeAllUrl;
    if (data.heading) heading.value = data.heading;
    if (data.subtitle) subtitle.value = data.subtitle;
    if (data.disclaimer) disclaimer.value = data.disclaimer;
    if (data.variant === 'products' || data.variant === 'before-after') variant.value = data.variant;
    if (data.benefits) {
      try {
        const parsed = JSON.parse(data.benefits) as string[];
        if (Array.isArray(parsed) && parsed.length) benefits.value = parsed;
      } catch {
        // ignore
      }
    }
    // data-slides fallback when :slides prop not available (e.g. when attributes not passed)
    if (data.slides) {
      try {
        const parsed = JSON.parse(data.slides) as SlideData[];
        const mapped = parsed.map((s) => {
          if (isBeforeAfterSlide(s)) {
            return { ...s, position: clampPosition((s as BeforeAfterSlide).position ?? 50) };
          }
          return s;
        });
        if (mapped.length) localSlides.value = mapped;
      } catch {
        // ignore
      }
    }
  }
  // Ensure slider always has content if slides prop/dataset parsing failed
  if (localSlides.value.length === 0) {
    localSlides.value = [{
      before: '/assets/img/treatments/trusted-experts.webp',
      after: '/assets/img/treatments/same-day-appointments.webp',
      altBefore: 'Before',
      altAfter: 'After',
      testimonial: 'Audrey lost 53lbs. with JRNYS',
      position: 50,
    }];
  }
});
</script>
